import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { pdf } from "@react-pdf/renderer";

import EntranceTestPaperPDF from "./EntranceTestPaperPDF";

// Voices Alert Audio Files
import voice20Minutes from "./audio/twentyminutes.mp3";
import voice10Minutes from "./audio/tenminutes.mp3";
import voice5Minutes from "./audio/fiveminutes.mp3";

import {
    FaArrowLeft,
    FaArrowRight,
    FaBookOpen,
    FaCheckCircle,
    FaExclamationTriangle,
    FaGraduationCap,
    FaPaperPlane,
    FaShieldAlt,
    FaSpinner,
    FaTrophy,
    FaClock,
    FaListOl,
    FaLock,
} from "react-icons/fa";

import api from "../api/api";

/* ============================================================
   BRAND
============================================================ */

const BRAND = {
    navy: "#031B35",
    deepNavy: "#021426",
    navyBlue: "#062A4B",
    blue: "#063A6B",
    black: "#071018",
    gold: "#F6C944",
    goldLight: "#FFE58A",
    yellow: "#EFA81C",
    orange: "#C0750D",
    green: "#4FAE22",
    red: "#C62828",
    text: "#172033",
    muted: "#687386",
    border: "#DCE4EE",
    background: "#F4F7FB",
    white: "#FFFFFF",
};

/* ============================================================
   PASS MARK
============================================================ */

const PASS_MARK = 50;

/* ============================================================
   VOICE ALERTS
============================================================ */

const VOICE_ALERTS = {
    1200: voice20Minutes,
    600: voice10Minutes,
    300: voice5Minutes,
};

/*
 * These are intentionally exact thresholds:
 *
 * 1200 seconds = 20 minutes
 *  600 seconds = 10 minutes
 *  300 seconds =  5 minutes
 */

/* ============================================================
   STORAGE
============================================================ */

const APPLICATION_NUMBER_KEY =
    "paf_entrance_application_number";

const APPLICATION_KEY =
    "paf_entrance_application";

const getAttemptStorageKey = (subject) =>
    `paf_entrance_exam_attempt_${subject}`;

const getAnswersStorageKey = (subject) =>
    `paf_entrance_exam_answers_${subject}`;

/* ============================================================
   API ENDPOINTS
============================================================ */

const getStartEndpoint = (
    applicationNumber,
    subject
) =>
    `/api/entrance_examination/applications/${encodeURIComponent(
        applicationNumber
    )}/exams/${encodeURIComponent(subject)}/start/`;

const getAvailableExamsEndpoint = (
    applicationNumber
) =>
    `/api/entrance_examination/applications/${encodeURIComponent(
        applicationNumber
    )}/exams/`;

const getStatusEndpoint = (
    applicationNumber,
    attemptId
) =>
    `/api/entrance_examination/applications/${encodeURIComponent(
        applicationNumber
    )}/attempts/${attemptId}/status/`;

const getSubmitEndpoint = (
    applicationNumber,
    attemptId
) =>
    `/api/entrance_examination/applications/${encodeURIComponent(
        applicationNumber
    )}/attempts/${attemptId}/submit/`;

const getResultEndpoint = (
    applicationNumber,
    attemptId
) =>
    `/api/entrance_examination/applications/${encodeURIComponent(
        applicationNumber
    )}/attempts/${attemptId}/result/`;

/* ============================================================
   SUBJECTS
============================================================ */

const SUBJECTS = {
    english: {
        label: "English Language",
        shortLabel: "English",
    },

    mathematics: {
        label: "Mathematics",
        shortLabel: "Mathematics",
    },
};

/* ============================================================
   HELPERS
============================================================ */

const normalizeApplicationNumber = (
    value
) =>
    String(value || "")
        .trim()
        .toUpperCase();

const readStoredApplication = () => {
    try {
        const stored =
            localStorage.getItem(
                APPLICATION_KEY
            );

        if (!stored) {
            return null;
        }

        const parsed =
            JSON.parse(stored);

        return parsed &&
            typeof parsed ===
                "object" &&
            !Array.isArray(parsed)
            ? parsed
            : null;
    } catch (error) {
        console.error(
            "Unable to read stored application:",
            error
        );

        return null;
    }
};

const getApplicantName = (
    application
) => {
    if (!application) {
        return "Applicant";
    }

    if (application.applicant_name) {
        return String(
            application.applicant_name
        );
    }

    return [
        application.first_name,
        application.middle_name,
        application.last_name,
    ]
        .filter(Boolean)
        .join(" ")
        .trim() || "Applicant";
};

const getErrorMessage = (
    error,
    fallback
) => {
    const data =
        error?.response?.data;

    if (!data) {
        return (
            error?.message ||
            fallback
        );
    }

    if (
        typeof data ===
        "string"
    ) {
        return data;
    }

    if (data.detail) {
        return String(
            data.detail
        );
    }

    if (data.message) {
        return String(
            data.message
        );
    }

    if (data.error) {
        return String(
            data.error
        );
    }

    const firstError =
        Object.values(
            data
        ).find(
            (value) =>
                typeof value ===
                    "string" ||
                Array.isArray(value)
        );

    if (
        Array.isArray(
            firstError
        )
    ) {
        return String(
            firstError[0] ||
                fallback
        );
    }

    if (firstError) {
        return String(
            firstError
        );
    }

    return fallback;
};

/* ============================================================
   ATTEMPT HELPERS
============================================================ */

const extractAttemptId = (
    data
) => {
    const raw =
        data?.attempt_id ??
        data?.attempt?.id ??
        data?.attempt?.attempt_id ??
        data?.result?.attempt_id ??
        data?.result?.attempt?.id ??
        data?.data?.attempt_id ??
        data?.data?.attempt?.id;

    const id = Number(raw);

    return Number.isInteger(id) &&
        id > 0
        ? id
        : null;
};

const normalizeAttemptStatus = (
    value
) =>
    String(value || "")
        .trim()
        .toLowerCase();

const isCompletedStatus = (
    status
) => {
    const normalized =
        normalizeAttemptStatus(
            status
        );

    return [
        "submitted",
        "completed",
        "expired",
    ].includes(
        normalized
    );
};

const isInProgressStatus = (
    status
) =>
    normalizeAttemptStatus(
        status
    ) === "in_progress" ||
    normalizeAttemptStatus(
        status
    ) === "in-progress" ||
    normalizeAttemptStatus(
        status
    ) === "started";

/* ============================================================
   QUESTION NORMALIZATION
============================================================ */

const normalizeQuestion = (
    question,
    index
) => {
    const options =
        question?.options || {};

    return {
        id:
            Number(
                question?.id
            ) || null,

        number:
            Number(
                question?.question_number ??
                    question?.number ??
                    index + 1
            ),

        section:
            question?.section ||
            "",

        question:
            question?.question_text ||
            question?.question ||
            question?.text ||
            "",

        options: {
            A:
                options.A ??
                options.a ??
                question?.option_a ??
                "",

            B:
                options.B ??
                options.b ??
                question?.option_b ??
                "",

            C:
                options.C ??
                options.c ??
                question?.option_c ??
                "",

            D:
                options.D ??
                options.d ??
                question?.option_d ??
                "",
        },

        marks:
            Number(
                question?.marks ??
                    1
            ),
    };
};

const normalizeQuestions = (
    data
) => {
    const raw =
        Array.isArray(data)
            ? data
            : data?.questions ||
              data?.exam?.questions ||
              data?.attempt?.questions ||
              data?.attempt?.exam?.questions ||
              data?.data?.questions ||
              data?.data?.exam?.questions ||
              data?.data?.attempt?.questions ||
              data?.data?.attempt?.exam?.questions ||
              [];

    if (
        !Array.isArray(raw)
    ) {
        return [];
    }

    return raw
        .map(
            normalizeQuestion
        )
        .filter(
            (question) =>
                question.id &&
                question.question
        );
};

/* ============================================================
   RESULT EXTRACTION
============================================================ */

const extractResult = (
    data
) => {
    if (!data) {
        return null;
    }

    if (
        data?.result &&
        typeof data.result ===
            "object"
    ) {
        return data.result;
    }

    if (
        data?.attempt?.result &&
        typeof data.attempt.result ===
            "object"
    ) {
        return data.attempt.result;
    }

    if (
        data?.data?.result &&
        typeof data.data.result ===
            "object"
    ) {
        return data.data.result;
    }

    /*
     * Some result endpoints return the
     * serialized result directly.
     */
    if (
        data?.obtained_marks !==
            undefined ||
        data?.score !== undefined ||
        data?.marks_obtained !==
            undefined
    ) {
        return data;
    }

    return null;
};

/* ============================================================
   EXAM EXTRACTION
============================================================ */

const extractAvailableExams = (
    data
) => {
    const exams =
        data?.exams ||
        data?.results ||
        data?.data ||
        data?.available_exams ||
        data?.examinations ||
        (Array.isArray(data)
            ? data
            : []);

    return Array.isArray(exams)
        ? exams
        : [];
};

const normalizeExamSubject = (
    exam
) =>
    String(
        exam?.subject ||
            exam?.subject_code ||
            exam?.code ||
            exam?.slug ||
            exam?.name ||
            exam?.title ||
            ""
    )
        .trim()
        .toLowerCase()
        .replace(
            /[_-]+/g,
            " "
        );

const findMatchingExam = (
    exams,
    subject
) => {
    const normalizedSubject =
        String(subject)
            .trim()
            .toLowerCase();

    return (
        exams.find(
            (exam) => {
                const value =
                    normalizeExamSubject(
                        exam
                    );

                return (
                    value ===
                        normalizedSubject ||
                    value.includes(
                        normalizedSubject
                    ) ||
                    normalizedSubject.includes(
                        value
                    )
                );
            }
        ) || null
    );
};

/* ============================================================
   EXAM ATTEMPT METADATA
============================================================ */

const getExamAttemptMetadata = (
    exam
) => {
    if (!exam) {
        return {
            attemptId: null,
            status: "",
            result: null,
            completed: false,
        };
    }

    const attempt =
        exam?.attempt ||
        exam?.current_attempt ||
        exam?.latest_attempt ||
        null;

    const attemptId =
        extractAttemptId(
            exam
        ) ||
        extractAttemptId(
            attempt
        );

    const status =
        normalizeAttemptStatus(
            exam?.attempt_status ||
                exam?.status ||
                attempt?.status ||
                ""
        );

    const result =
        extractResult(
            exam?.result
                ? exam
                : attempt
        );

    const completed =
        Boolean(
            exam?.is_completed ||
                exam?.completed ||
                attempt?.is_completed ||
                attempt?.completed ||
                isCompletedStatus(
                    status
                ) ||
                result
        );

    return {
        attemptId,
        status,
        result,
        completed,
    };
};

/* ============================================================
   TIME
============================================================ */

const formatSeconds = (
    seconds
) => {
    const safe =
        Math.max(
            0,
            Number(seconds) || 0
        );

    const hours =
        Math.floor(
            safe / 3600
        );

    const minutes =
        Math.floor(
            (safe % 3600) / 60
        );

    const secs =
        safe % 60;

    if (hours > 0) {
        return `${String(
            hours
        ).padStart(
            2,
            "0"
        )}:${String(
            minutes
        ).padStart(
            2,
            "0"
        )}:${String(
            secs
        ).padStart(
            2,
            "0"
        )}`;
    }

    return `${String(
        minutes
    ).padStart(
        2,
        "0"
    )}:${String(
        secs
    ).padStart(
        2,
        "0"
    )}`;
};

/* ============================================================
   STORAGE - ATTEMPT
============================================================ */

const readStoredAttemptId = (
    subject
) => {
    try {
        const value =
            localStorage.getItem(
                getAttemptStorageKey(
                    subject
                )
            );

        if (!value) {
            return null;
        }

        const id =
            Number(value);

        return Number.isInteger(
            id
        ) && id > 0
            ? id
            : null;
    } catch {
        return null;
    }
};

const saveStoredAttemptId = (
    subject,
    attemptId
) => {
    try {
        if (
            Number.isInteger(
                Number(attemptId)
            ) &&
            Number(attemptId) > 0
        ) {
            localStorage.setItem(
                getAttemptStorageKey(
                    subject
                ),
                String(
                    attemptId
                )
            );
        }
    } catch (error) {
        console.warn(
            "Unable to save entrance examination attempt ID:",
            error
        );
    }
};

const clearStoredAttemptId = (
    subject
) => {
    try {
        localStorage.removeItem(
            getAttemptStorageKey(
                subject
            )
        );
    } catch {}
};

/* ============================================================
   STORAGE - ANSWERS
============================================================ */

const readStoredAnswers = (
    subject
) => {
    try {
        const value =
            localStorage.getItem(
                getAnswersStorageKey(
                    subject
                )
            );

        if (!value) {
            return {};
        }

        const parsed =
            JSON.parse(value);

        if (
            !parsed ||
            typeof parsed !==
                "object" ||
            Array.isArray(parsed)
        ) {
            return {};
        }

        return parsed;
    } catch {
        return {};
    }
};

const clearStoredAnswers = (
    subject
) => {
    try {
        localStorage.removeItem(
            getAnswersStorageKey(
                subject
            )
        );
    } catch {}
};

/* ============================================================
   AUDIO
============================================================ */

const playVoiceAlert = (
    threshold
) => {
    const source =
        VOICE_ALERTS[
            threshold
        ];

    if (!source) {
        return;
    }

    try {
        const audio =
            new Audio(
                source
            );

        audio.preload =
            "auto";

        audio.volume =
            1;

        audio.currentTime =
            0;

        const playPromise =
            audio.play();

        if (
            playPromise &&
            typeof playPromise.catch ===
                "function"
        ) {
            playPromise.catch(
                (error) => {
                    /*
                     * Browsers can block audio
                     * until the applicant has
                     * interacted with the page.
                     *
                     * We deliberately do not
                     * use an external service.
                     */
                    console.warn(
                        `Local ${threshold / 60}-minute voice alert could not play:`,
                        error
                    );
                }
            );
        }
    } catch (error) {
        console.warn(
            "Unable to play local entrance examination voice alert:",
            error
        );
    }
};

/* ============================================================
   MAIN PAGE
============================================================ */

const EntranceExaminationPage = ({
    subject: suppliedSubject,
}) => {
    const navigate =
        useNavigate();

    const params =
        useParams();

    const subject =
        String(
            suppliedSubject ||
                params.subject ||
                ""
        )
            .trim()
            .toLowerCase();

    const subjectInfo =
        SUBJECTS[subject];

    const storedApplication =
        useMemo(
            () =>
                readStoredApplication(),
            []
        );

    const applicationNumber =
        normalizeApplicationNumber(
            localStorage.getItem(
                APPLICATION_NUMBER_KEY
            ) ||
                storedApplication?.application_number
        );

    const [
        application,
        setApplication,
    ] = useState(
        storedApplication
    );

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        starting,
        setStarting,
    ] = useState(false);

    const [
        submitting,
        setSubmitting,
    ] = useState(false);

    const [
        error,
        setError,
    ] = useState("");

    const [
        attemptId,
        setAttemptId,
    ] = useState(
        () =>
            readStoredAttemptId(
                subject
            )
    );

    const [
        questions,
        setQuestions,
    ] = useState([]);

    const [
        answers,
        setAnswers,
    ] = useState(
        () =>
            readStoredAnswers(
                subject
            )
    );

    const [
        currentIndex,
        setCurrentIndex,
    ] = useState(0);

    const [
        expiresAt,
        setExpiresAt,
    ] = useState(null);

    const [
        serverOffset,
        setServerOffset,
    ] = useState(0);

    const [
        remainingSeconds,
        setRemainingSeconds,
    ] = useState(0);

    const [
        attemptStatus,
        setAttemptStatus,
    ] = useState(
        "in_progress"
    );

    const [
        result,
        setResult,
    ] = useState(null);

    const [
        availableExam,
        setAvailableExam,
    ] = useState(null);

    const [
        questionsLoading,
        setQuestionsLoading,
    ] = useState(false);

    const [
        questionRetryCount,
        setQuestionRetryCount,
    ] = useState(0);

    const submitLockRef =
        useRef(false);

    const initializedRef =
        useRef(false);

    const questionRetryTimerRef =
        useRef(null);

    const status404CountRef =
        useRef(0);

    /*
     * Prevents the same voice warning from
     * playing more than once during an attempt.
     */
    const voiceAlertsPlayedRef =
        useRef(
            new Set()
        );

    /*
     * Used to detect the exact moment the
     * timer crosses 20, 10, or 5 minutes.
     */
    const previousRemainingSecondsRef =
        useRef(null);

    const isValidSubject =
        Boolean(subjectInfo);

    const currentQuestion =
        questions[
            currentIndex
        ] || null;

    const answeredCount =
        questions.reduce(
            (
                count,
                question
            ) =>
                answers[
                    String(
                        question.id
                    )
                ]
                    ? count + 1
                    : count,
            0
        );

    const unansweredCount =
        Math.max(
            0,
            questions.length -
                answeredCount
        );

    const progress =
        questions.length
            ? Math.round(
                  (answeredCount /
                      questions.length) *
                      100
              )
            : 0;

    /* ========================================================
       SERVER TIME
    ======================================================== */

    const applyServerTime =
        useCallback(
            (data) => {
                const serverTime =
                    data?.server_time ||
                    data?.server_now ||
                    data?.current_time;

                if (!serverTime) {
                    return;
                }

                const serverDate =
                    new Date(
                        serverTime
                    );

                if (
                    Number.isNaN(
                        serverDate.getTime()
                    )
                ) {
                    return;
                }

                setServerOffset(
                    serverDate.getTime() -
                        Date.now()
                );
            },
            []
        );

    /* ========================================================
       ALERT
    ======================================================== */

    const showAlert =
        useCallback(
            ({
                icon,
                title,
                text,
                confirmButtonText =
                    "OK",
                confirmButtonColor =
                    BRAND.navy,
            }) =>
                Swal.fire({
                    icon,
                    title,
                    text,
                    confirmButtonText,
                    confirmButtonColor,
                    background:
                        BRAND.white,
                    color:
                        BRAND.text,
                    allowOutsideClick:
                        false,
                    customClass: {
                        popup:
                            "pee-swal-popup",
                        title:
                            "pee-swal-title",
                        confirmButton:
                            "pee-swal-button",
                    },
                }),
            []
        );

    /* ========================================================
       SAVE ANSWERS
    ======================================================== */

    const persistAnswers =
        useCallback(
            (nextAnswers) => {
                try {
                    localStorage.setItem(
                        getAnswersStorageKey(
                            subject
                        ),
                        JSON.stringify(
                            nextAnswers
                        )
                    );
                } catch (
                    storageError
                ) {
                    console.error(
                        "Unable to save examination answers locally:",
                        storageError
                    );
                }
            },
            [subject]
        );

    /* ========================================================
       BUILD SUBMISSION
    ======================================================== */

    const buildSubmitAnswers =
        useCallback(
            (
                answersToSubmit
            ) =>
                questions
                    .map(
                        (
                            question
                        ) => {
                            const selected =
                                String(
                                    answersToSubmit?.[
                                        String(
                                            question.id
                                        )
                                    ] || ""
                                ).toUpperCase();

                            return {
                                question_id:
                                    question.id,

                                selected_answer:
                                    [
                                        "A",
                                        "B",
                                        "C",
                                        "D",
                                    ].includes(
                                        selected
                                    )
                                        ? selected
                                        : "",
                            };
                        }
                    )
                    .filter(
                        (
                            answer
                        ) =>
                            answer.question_id
                    ),
            [questions]
        );

    /* ========================================================
       APPLY RESULT
    ======================================================== */

    const applyResult =
        useCallback(
            (
                resultData,
                suppliedAttemptId
            ) => {
                const extracted =
                    extractResult(
                        resultData
                    );

                if (
                    extracted
                ) {
                    setResult(
                        extracted
                    );
                }

                if (
                    suppliedAttemptId
                ) {
                    const numericId =
                        Number(
                            suppliedAttemptId
                        );

                    if (
                        Number.isInteger(
                            numericId
                        ) &&
                        numericId > 0
                    ) {
                        setAttemptId(
                            numericId
                        );

                        saveStoredAttemptId(
                            subject,
                            numericId
                        );
                    }
                }

                setAttemptStatus(
                    "submitted"
                );

                setQuestionsLoading(
                    false
                );

                setQuestions(
                    []
                );

                clearStoredAnswers(
                    subject
                );

                submitLockRef.current =
                    false;

                return Boolean(
                    extracted
                );
            },
            [subject]
        );

    /* ========================================================
       LOAD RESULT
    ======================================================== */

    const loadResult =
        useCallback(
            async (
                suppliedAttemptId
            ) => {
                if (
                    !applicationNumber ||
                    !suppliedAttemptId
                ) {
                    return false;
                }

                try {
                    const response =
                        await api.get(
                            getResultEndpoint(
                                applicationNumber,
                                suppliedAttemptId
                            )
                        );

                    const data =
                        response?.data ||
                        {};

                    applyServerTime(
                        data
                    );

                    const extracted =
                        extractResult(
                            data
                        );

                    if (
                        !extracted
                    ) {
                        console.warn(
                            "Result endpoint returned no recognizable result:",
                            data
                        );

                        return false;
                    }

                    setResult(
                        extracted
                    );

                    setAttemptStatus(
                        "submitted"
                    );

                    setQuestionsLoading(
                        false
                    );

                    setQuestions(
                        []
                    );

                    clearStoredAnswers(
                        subject
                    );

                    saveStoredAttemptId(
                        subject,
                        Number(
                            suppliedAttemptId
                        )
                    );

                    submitLockRef.current =
                        false;

                    return true;
                } catch (
                    resultError
                ) {
                    console.error(
                        "Unable to load examination result:",
                        resultError
                    );

                    return false;
                }
            },
            [
                applicationNumber,
                applyServerTime,
                subject,
            ]
        );

    /* ========================================================
       STATUS REQUEST
    ======================================================== */

    const refreshAttemptStatus =
        useCallback(
            async (
                suppliedAttemptId,
                options = {}
            ) => {
                if (
                    !applicationNumber ||
                    !suppliedAttemptId
                ) {
                    return null;
                }

                const {
                    silent = false,
                } = options;

                try {
                    const response =
                        await api.get(
                            getStatusEndpoint(
                                applicationNumber,
                                suppliedAttemptId
                            )
                        );

                    const data =
                        response?.data ||
                        {};

                    status404CountRef.current =
                        0;

                    applyServerTime(
                        data
                    );

                    if (
                        data?.application
                    ) {
                        setApplication(
                            data.application
                        );

                        try {
                            localStorage.setItem(
                                APPLICATION_KEY,
                                JSON.stringify(
                                    data.application
                                )
                            );
                        } catch {}
                    }

                    const returnedStatus =
                        normalizeAttemptStatus(
                            data?.status ||
                                data?.attempt
                                    ?.status ||
                                ""
                        );

                    if (
                        data?.expires_at
                    ) {
                        setExpiresAt(
                            data.expires_at
                        );
                    }

                    if (
                        data?.remaining_seconds !==
                        undefined
                    ) {
                        setRemainingSeconds(
                            Math.max(
                                0,
                                Number(
                                    data.remaining_seconds
                                ) || 0
                            )
                        );
                    }

                    if (
                        returnedStatus
                    ) {
                        setAttemptStatus(
                            returnedStatus
                        );
                    }

                    const returnedQuestions =
                        normalizeQuestions(
                            data
                        );

                    if (
                        returnedQuestions.length
                    ) {
                        setQuestions(
                            returnedQuestions
                        );

                        setQuestionRetryCount(
                            0
                        );

                        setQuestionsLoading(
                            false
                        );
                    }

                    const returnedResult =
                        extractResult(
                            data
                        );

                    if (
                        returnedResult
                    ) {
                        setResult(
                            returnedResult
                        );
                    }

                    /*
                     * A completed attempt returned
                     * directly by the status endpoint
                     * must immediately become the
                     * result state.
                     */
                    if (
                        isCompletedStatus(
                            returnedStatus
                        ) ||
                        returnedResult
                    ) {
                        setAttemptStatus(
                            "submitted"
                        );

                        setQuestions(
                            []
                        );

                        setQuestionsLoading(
                            false
                        );

                        clearStoredAnswers(
                            subject
                        );
                    }

                    if (!silent) {
                        setError("");
                    }

                    return data;
                } catch (
                    statusError
                ) {
                    const status =
                        statusError
                            ?.response
                            ?.status;

                    /*
                     * A 404 means this particular
                     * attempt ID cannot currently be
                     * resolved.
                     *
                     * DO NOT immediately delete it.
                     *
                     * The initialization logic will
                     * safely fall back to the start
                     * endpoint, whose 409 response can
                     * identify an already-submitted
                     * attempt.
                     */
                    if (
                        status === 404
                    ) {
                        status404CountRef.current +=
                            1;

                        console.warn(
                            "Entrance examination attempt status unavailable:",
                            status404CountRef.current
                        );

                        return null;
                    }

                    if (!silent) {
                        console.warn(
                            "Entrance examination status request temporarily failed:",
                            statusError
                        );
                    }

                    return null;
                }
            },
            [
                applicationNumber,
                applyServerTime,
                subject,
            ]
        );

    /* ========================================================
       RESOLVE 409 START RESPONSE
    ======================================================== */

    const resolveStartConflict =
        useCallback(
            async (
                requestError
            ) => {
                const data =
                    requestError
                        ?.response
                        ?.data ||
                    {};

                const conflictAttemptId =
                    extractAttemptId(
                        data
                    );

                const conflictStatus =
                    normalizeAttemptStatus(
                        data?.status ||
                            data?.attempt
                                ?.status ||
                            data?.attempt_status ||
                            ""
                    );

                /*
                 * If backend gives us the attempt ID,
                 * save it immediately.
                 */
                if (
                    conflictAttemptId
                ) {
                    setAttemptId(
                        conflictAttemptId
                    );

                    saveStoredAttemptId(
                        subject,
                        conflictAttemptId
                    );
                }

                /*
                 * Submitted/completed/expired:
                 * retrieve result immediately.
                 */
                if (
                    conflictAttemptId &&
                    (
                        isCompletedStatus(
                            conflictStatus
                        ) ||
                        data?.result ||
                        data?.attempt
                            ?.result
                    )
                ) {
                    const directResult =
                        extractResult(
                            data
                        );

                    if (
                        directResult
                    ) {
                        applyResult(
                            data,
                            conflictAttemptId
                        );

                        return true;
                    }

                    const loaded =
                        await loadResult(
                            conflictAttemptId
                        );

                    if (
                        loaded
                    ) {
                        return true;
                    }
                }

                /*
                 * Even if the 409 response did not
                 * explicitly say "submitted", an
                 * attempt ID means we should inspect
                 * its status/result before trying to
                 * create anything else.
                 */
                if (
                    conflictAttemptId
                ) {
                    const statusData =
                        await refreshAttemptStatus(
                            conflictAttemptId,
                            {
                                silent:
                                    true,
                            }
                        );

                    if (
                        statusData
                    ) {
                        const status =
                            normalizeAttemptStatus(
                                statusData?.status ||
                                    statusData?.attempt
                                        ?.status ||
                                    ""
                            );

                        if (
                            isCompletedStatus(
                                status
                            ) ||
                            extractResult(
                                statusData
                            )
                        ) {
                            const loaded =
                                await loadResult(
                                    conflictAttemptId
                                );

                            if (
                                loaded
                            ) {
                                return true;
                            }
                        }

                        if (
                            isInProgressStatus(
                                status
                            )
                        ) {
                            setAttemptStatus(
                                "in_progress"
                            );

                            const statusQuestions =
                                normalizeQuestions(
                                    statusData
                                );

                            if (
                                statusQuestions.length
                            ) {
                                setQuestions(
                                    statusQuestions
                                );

                                setQuestionsLoading(
                                    false
                                );
                            }

                            return true;
                        }
                    }

                    /*
                     * Last result attempt. This handles
                     * a backend that returns 409 with
                     * an attempt ID but no status.
                     */
                    const loaded =
                        await loadResult(
                            conflictAttemptId
                        );

                    if (
                        loaded
                    ) {
                        return true;
                    }
                }

                return false;
            },
            [
                subject,
                applyResult,
                loadResult,
                refreshAttemptStatus,
            ]
        );

    /* ========================================================
       WAIT FOR QUESTIONS
    ======================================================== */

    const waitForQuestions =
    useCallback(
        async (
            suppliedAttemptId
        ) => {
            if (
                !suppliedAttemptId ||
                !applicationNumber
            ) {
                return false;
            }

            /*
             * If questions are already available,
             * there is nothing else to retrieve.
             */
            if (
                questions.length > 0
            ) {
                setQuestionsLoading(false);
                return true;
            }

            setQuestionsLoading(true);
            setError("");

            /*
             * IMPORTANT:
             *
             * The status endpoint can restore the attempt
             * and timer without returning the question list.
             *
             * Therefore we MUST try the start/resume endpoint
             * for the EXISTING attempt instead of endlessly
             * polling /status/.
             */
            try {
                const resumeResponse =
                    await api.post(
                        getStartEndpoint(
                            applicationNumber,
                            subject
                        ),
                        {
                            attempt_id:
                                Number(
                                    suppliedAttemptId
                                ),
                            resume: true,
                        }
                    );

                const resumeData =
                    resumeResponse?.data ||
                    {};

                applyServerTime(
                    resumeData
                );

                /*
                 * Keep the exact same attempt ID.
                 */
                const returnedAttemptId =
                    extractAttemptId(
                        resumeData
                    );

                const activeAttemptId =
                    returnedAttemptId ||
                    Number(
                        suppliedAttemptId
                    );

                if (
                    Number.isInteger(
                        activeAttemptId
                    ) &&
                    activeAttemptId > 0
                ) {
                    setAttemptId(
                        activeAttemptId
                    );

                    saveStoredAttemptId(
                        subject,
                        activeAttemptId
                    );
                }

                /*
                 * Restore application information if
                 * returned by the backend.
                 */
                if (
                    resumeData?.application
                ) {
                    setApplication(
                        resumeData.application
                    );

                    try {
                        localStorage.setItem(
                            APPLICATION_KEY,
                            JSON.stringify(
                                resumeData.application
                            )
                        );
                    } catch {}
                }

                /*
                 * Restore exam metadata.
                 */
                const resumedExam =
                    resumeData?.exam ||
                    resumeData?.attempt
                        ?.exam ||
                    null;

                if (
                    resumedExam
                ) {
                    setAvailableExam(
                        resumedExam
                    );
                }

                /*
                 * Restore timer.
                 */
                if (
                    resumeData?.expires_at
                ) {
                    setExpiresAt(
                        resumeData.expires_at
                    );
                }

                if (
                    resumeData?.remaining_seconds !==
                    undefined
                ) {
                    setRemainingSeconds(
                        Math.max(
                            0,
                            Number(
                                resumeData.remaining_seconds
                            ) || 0
                        )
                    );
                }

                /*
                 * Restore status.
                 */
                const resumedStatus =
                    normalizeAttemptStatus(
                        resumeData?.status ||
                            resumeData?.attempt
                                ?.status ||
                            "in_progress"
                    );

                if (
                    isCompletedStatus(
                        resumedStatus
                    ) ||
                    extractResult(
                        resumeData
                    )
                ) {
                    const directResult =
                        extractResult(
                            resumeData
                        );

                    if (
                        directResult
                    ) {
                        applyResult(
                            resumeData,
                            activeAttemptId
                        );

                        return false;
                    }

                    const loaded =
                        await loadResult(
                            activeAttemptId
                        );

                    return loaded;
                }

                setAttemptStatus(
                    "in_progress"
                );

                /*
                 * THIS IS THE IMPORTANT PART:
                 *
                 * Get questions from the resume response.
                 */
                const resumedQuestions =
                    normalizeQuestions(
                        resumeData
                    );

                if (
                    resumedQuestions.length >
                    0
                ) {
                    setQuestions(
                        resumedQuestions
                    );

                    setQuestionsLoading(
                        false
                    );

                    setQuestionRetryCount(
                        0
                    );

                    /*
                     * Restore locally saved answers.
                     */
                    const savedAnswers =
                        readStoredAnswers(
                            subject
                        );

                    if (
                        Object.keys(
                            savedAnswers
                        ).length > 0
                    ) {
                        setAnswers(
                            savedAnswers
                        );
                    }

                    previousRemainingSecondsRef.current =
                        null;

                    return true;
                }
            } catch (
                resumeError
            ) {
                const resumeStatus =
                    resumeError
                        ?.response
                        ?.status;

                /*
                 * A 409 is NOT necessarily an error.
                 *
                 * It may mean the backend already has
                 * the active attempt. Try the status endpoint
                 * one more time and inspect every possible
                 * question location.
                 */
                if (
                    resumeStatus ===
                    409
                ) {
                    console.warn(
                        "Resume returned 409. Checking existing attempt:",
                        resumeError
                    );
                } else {
                    console.warn(
                        "Direct examination resume request failed:",
                        resumeError
                    );
                }
            }

            /*
             * FALLBACK:
             *
             * Check the status endpoint again.
             *
             * Some backend versions return questions only
             * after the resume request has initialized the
             * attempt.
             */
            try {
                const statusData =
                    await refreshAttemptStatus(
                        suppliedAttemptId,
                        {
                            silent: true,
                        }
                    );

                if (
                    statusData
                ) {
                    const statusQuestions =
                        normalizeQuestions(
                            statusData
                        );

                    if (
                        statusQuestions.length >
                        0
                    ) {
                        setQuestions(
                            statusQuestions
                        );

                        setQuestionsLoading(
                            false
                        );

                        setQuestionRetryCount(
                            0
                        );

                        const savedAnswers =
                            readStoredAnswers(
                                subject
                            );

                        if (
                            Object.keys(
                                savedAnswers
                            ).length >
                            0
                        ) {
                            setAnswers(
                                savedAnswers
                            );
                        }

                        return true;
                    }

                    const returnedResult =
                        extractResult(
                            statusData
                        );

                    const returnedStatus =
                        normalizeAttemptStatus(
                            statusData?.status ||
                                statusData?.attempt
                                    ?.status ||
                                ""
                        );

                    if (
                        returnedResult ||
                        isCompletedStatus(
                            returnedStatus
                        )
                    ) {
                        return await loadResult(
                            suppliedAttemptId
                        );
                    }

                    /*
                     * Keep timer information even when
                     * questions are still unavailable.
                     */
                    if (
                        statusData?.expires_at
                    ) {
                        setExpiresAt(
                            statusData.expires_at
                        );
                    }

                    if (
                        statusData?.remaining_seconds !==
                        undefined
                    ) {
                        setRemainingSeconds(
                            Math.max(
                                0,
                                Number(
                                    statusData.remaining_seconds
                                ) || 0
                            )
                        );
                    }
                }
            } catch (
                fallbackError
            ) {
                console.warn(
                    "Final examination question recovery failed:",
                    fallbackError
                );
            }

            /*
             * DO NOT leave the applicant in an endless
             * loading loop.
             *
             * We make the question retrieval retryable.
             */
            setQuestionsLoading(
                false
            );

            setQuestionRetryCount(
                (value) =>
                    value + 1
            );

            return false;
        },
        [
            applicationNumber,
            subject,
            questions.length,
            applyServerTime,
            applyResult,
            loadResult,
            refreshAttemptStatus,
        ]
    );

    /* ========================================================
       START EXAM
    ======================================================== */

    const startExam =
    useCallback(
        async () => {
            if (
                !applicationNumber ||
                !isValidSubject
            ) {
                return false;
            }

            setStarting(true);
            setLoading(true);
            setError("");

            try {
                const response =
                    await api.post(
                        getStartEndpoint(
                            applicationNumber,
                            subject
                        )
                    );

                const data =
                    response?.data || {};

                applyServerTime(data);

                if (
                    data?.application
                ) {
                    setApplication(
                        data.application
                    );

                    try {
                        localStorage.setItem(
                            APPLICATION_KEY,
                            JSON.stringify(
                                data.application
                            )
                        );
                    } catch {}
                }

                /*
                 * --------------------------------------------------
                 * ATTEMPT
                 * --------------------------------------------------
                 */

                const returnedAttemptId =
                    extractAttemptId(data);

                if (
                    !returnedAttemptId
                ) {
                    throw new Error(
                        "The examination server did not return a valid examination attempt."
                    );
                }

                setAttemptId(
                    returnedAttemptId
                );

                saveStoredAttemptId(
                    subject,
                    returnedAttemptId
                );

                /*
                 * --------------------------------------------------
                 * EXAM
                 * --------------------------------------------------
                 */

                const exam =
                    data?.exam ||
                    data?.attempt?.exam ||
                    null;

                if (exam) {
                    setAvailableExam(
                        exam
                    );
                }

                /*
                 * --------------------------------------------------
                 * QUESTIONS
                 *
                 * IMPORTANT:
                 * The backend /start/ endpoint ALWAYS returns the
                 * questions for an active attempt.
                 * --------------------------------------------------
                 */

                const returnedQuestions =
                    normalizeQuestions(data);

                if (
                    returnedQuestions.length
                ) {
                    setQuestions(
                        returnedQuestions
                    );

                    setQuestionsLoading(
                        false
                    );
                } else {
                    /*
                     * Do NOT call waitForQuestions().
                     *
                     * If this happens, the backend response did not
                     * contain questions and we should show a controlled
                     * recovery message instead of an endless loader.
                     */
                    setQuestions([]);

                    setQuestionsLoading(
                        true
                    );
                }

                /*
                 * --------------------------------------------------
                 * TIMER
                 * --------------------------------------------------
                 */

                if (
                    data?.expires_at
                ) {
                    setExpiresAt(
                        data.expires_at
                    );
                }

                if (
                    data?.remaining_seconds !==
                    undefined
                ) {
                    setRemainingSeconds(
                        Math.max(
                            0,
                            Number(
                                data.remaining_seconds
                            ) || 0
                        )
                    );
                }

                /*
                 * --------------------------------------------------
                 * STATUS
                 * --------------------------------------------------
                 */

                const returnedStatus =
                    normalizeAttemptStatus(
                        data?.status ||
                            data?.attempt
                                ?.status ||
                            "in_progress"
                    );

                /*
                 * --------------------------------------------------
                 * COMPLETED ATTEMPT
                 * --------------------------------------------------
                 */

                if (
                    isCompletedStatus(
                        returnedStatus
                    ) ||
                    extractResult(data)
                ) {
                    const loaded =
                        extractResult(data)
                            ? applyResult(
                                  data,
                                  returnedAttemptId
                              )
                            : await loadResult(
                                  returnedAttemptId
                              );

                    if (loaded) {
                        setQuestions([]);
                        setQuestionsLoading(
                            false
                        );

                        return true;
                    }
                }

                /*
                 * --------------------------------------------------
                 * ACTIVE ATTEMPT
                 * --------------------------------------------------
                 */

                setAttemptStatus(
                    "in_progress"
                );

                /*
                 * --------------------------------------------------
                 * RESTORE ANSWERS
                 * --------------------------------------------------
                 */

                const storedAnswers =
                    readStoredAnswers(
                        subject
                    );

                if (
                    Object.keys(
                        storedAnswers
                    ).length
                ) {
                    setAnswers(
                        storedAnswers
                    );
                }

                previousRemainingSecondsRef.current =
                    null;

                /*
                 * --------------------------------------------------
                 * IMPORTANT:
                 *
                 * No waitForQuestions().
                 *
                 * /start/ already returns the questions.
                 * --------------------------------------------------
                 */

                return true;
            } catch (
                requestError
            ) {
                const responseStatus =
                    requestError
                        ?.response
                        ?.status;

                /*
                 * --------------------------------------------------
                 * EXISTING COMPLETED ATTEMPT
                 * --------------------------------------------------
                 */

                if (
                    responseStatus === 409
                ) {
                    const resolved =
                        await resolveStartConflict(
                            requestError
                        );

                    if (resolved) {
                        setError("");
                        return true;
                    }
                }

                console.error(
                    "Entrance examination start failed:",
                    requestError
                );

                setError(
                    getErrorMessage(
                        requestError,
                        "We could not start your examination."
                    )
                );

                return false;
            } finally {
                setStarting(false);
                setLoading(false);
            }
        },
        [
            applicationNumber,
            isValidSubject,
            subject,
            applyServerTime,
            loadResult,
            applyResult,
            resolveStartConflict,
        ]
    );

    /* ========================================================
       INITIALIZE
    ======================================================== */

const initializeExam =
    useCallback(
        async () => {
            if (
                initializedRef.current
            ) {
                return;
            }

            if (
                !isValidSubject
            ) {
                setLoading(false);
                return;
            }

            if (
                !applicationNumber
            ) {
                navigate("/", {
                    replace: true,
                });

                return;
            }

            initializedRef.current =
                true;

            setLoading(true);
            setQuestionsLoading(true);
            setError("");

            /*
             * ======================================================
             * STEP 1
             * Check available examinations.
             * ======================================================
             */

            try {
                const availableResponse =
                    await api.get(
                        getAvailableExamsEndpoint(
                            applicationNumber
                        )
                    );

                const availableData =
                    availableResponse?.data ||
                    {};

                applyServerTime(
                    availableData
                );

                const rawExams =
                    extractAvailableExams(
                        availableData
                    );

                const matchingExam =
                    findMatchingExam(
                        rawExams,
                        subject
                    );

                if (
                    matchingExam
                ) {
                    setAvailableExam(
                        matchingExam
                    );

                    if (
                        matchingExam?.application
                    ) {
                        setApplication(
                            matchingExam.application
                        );
                    }

                    /*
                     * ------------------------------------------------
                     * Backend already tells us if an attempt exists.
                     * ------------------------------------------------
                     */

                    const metadata =
                        getExamAttemptMetadata(
                            matchingExam
                        );

                    if (
                        metadata.attemptId
                    ) {
                        const existingAttemptId =
                            String(
                                metadata.attemptId
                            );

                        setAttemptId(
                            existingAttemptId
                        );

                        saveStoredAttemptId(
                            subject,
                            existingAttemptId
                        );

                        /*
                         * --------------------------------------------
                         * COMPLETED
                         * --------------------------------------------
                         */

                        if (
                            metadata.completed
                        ) {
                            if (
                                metadata.result
                            ) {
                                setResult(
                                    metadata.result
                                );

                                setAttemptStatus(
                                    "submitted"
                                );

                                setQuestions([]);
                                setQuestionsLoading(
                                    false
                                );

                                clearStoredAnswers(
                                    subject
                                );

                                setLoading(false);

                                return;
                            }

                            const loaded =
                                await loadResult(
                                    existingAttemptId
                                );

                            if (
                                loaded
                            ) {
                                setQuestionsLoading(
                                    false
                                );

                                setLoading(false);

                                return;
                            }
                        }

                        /*
                         * --------------------------------------------
                         * ACTIVE ATTEMPT
                         *
                         * IMPORTANT:
                         *
                         * Do NOT call status and then wait for
                         * questions.
                         *
                         * Simply call /start/ again.
                         *
                         * The backend detects the existing attempt
                         * and returns its original questions.
                         * --------------------------------------------
                         */

                        if (
                            isInProgressStatus(
                                metadata.status
                            )
                        ) {
                            const resumed =
                                await startExam();

                            if (
                                resumed
                            ) {
                                setLoading(false);

                                return;
                            }
                        }
                    }
                }
            } catch (
                availabilityError
            ) {
                console.warn(
                    "Entrance examination availability request temporarily failed:",
                    availabilityError
                );
            }

            /*
             * ======================================================
             * STEP 2
             *
             * Check locally stored attempt.
             * ======================================================
             */

            const storedAttemptId =
                readStoredAttemptId(
                    subject
                );

            if (
                storedAttemptId
            ) {
                const localAttemptId =
                    String(
                        storedAttemptId
                    );

                setAttemptId(
                    localAttemptId
                );

                /*
                 * --------------------------------------------------
                 * Check whether the stored attempt is completed.
                 * --------------------------------------------------
                 */

                try {
                    const statusData =
                        await refreshAttemptStatus(
                            localAttemptId,
                            {
                                silent: true,
                            }
                        );

                    if (
                        statusData
                    ) {
                        const status =
                            normalizeAttemptStatus(
                                statusData?.status ||
                                    statusData?.attempt
                                        ?.status ||
                                    ""
                            );

                        /*
                         * Completed attempt.
                         */
                        if (
                            isCompletedStatus(
                                status
                            ) ||
                            extractResult(
                                statusData
                            )
                        ) {
                            const loaded =
                                await loadResult(
                                    localAttemptId
                                );

                            if (
                                loaded
                            ) {
                                setQuestionsLoading(
                                    false
                                );

                                setLoading(false);

                                return;
                            }
                        }

                        /*
                         * Active attempt.
                         *
                         * We do NOT use waitForQuestions().
                         *
                         * /start/ will find this exact attempt and
                         * return its questions.
                         */
                        if (
                            isInProgressStatus(
                                status
                            )
                        ) {
                            const resumed =
                                await startExam();

                            if (
                                resumed
                            ) {
                                setLoading(false);

                                return;
                            }
                        }
                    }
                } catch (
                    storedStatusError
                ) {
                    console.warn(
                        "Stored entrance examination attempt could not be checked:",
                        storedStatusError
                    );
                }
            }

            /*
             * ======================================================
             * STEP 3
             *
             * No existing active attempt could be restored.
             *
             * Start/resume endpoint itself will decide whether to
             * create a new attempt or return the existing one.
             * ======================================================
             */

            const started =
                await startExam();

            if (
                !started
            ) {
                setQuestionsLoading(
                    false
                );
            }

            setLoading(false);
        },
        [
            applicationNumber,
            subject,
            isValidSubject,
            navigate,
            applyServerTime,
            loadResult,
            refreshAttemptStatus,
            startExam,
        ]
    );


    useEffect(() => {
        initializeExam();

        return () => {
            if (
                questionRetryTimerRef.current
            ) {
                window.clearTimeout(
                    questionRetryTimerRef.current
                );
            }
        };
    }, [
        initializeExam,
    ]);

    /* ========================================================
       ANSWER SELECTION
    ======================================================== */


const selectAnswer = useCallback(
    async (option) => {
        if (
            submitting ||
            attemptStatus !== "in_progress" ||
            !currentQuestion
        ) {
            return;
        }

        const questionId = String(currentQuestion.id);

        const nextAnswers = {
            ...answers,
            [questionId]: option,
        };

        // Update the examination UI immediately.
        setAnswers(nextAnswers);
        persistAnswers(nextAnswers);

        try {
            await api.post(
                `/api/liveentrancemonitor/applications/${encodeURIComponent(
                    applicationNumber
                )}/attempts/${attemptId}/answer/`,
                {
                    question_id: currentQuestion.id,
                    selected_answer: option,
                }
            );
        } catch (error) {
            console.error(
                "Failed to save live examination answer:",
                error
            );

            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.question_id?.[0] ||
                "Your answer could not be synchronized with the examination server. Please check your connection.";

            Swal.fire({
                icon: "warning",
                title: "Answer Synchronization Issue",
                text: message,
                confirmButtonText: "OK",
                confirmButtonColor: "#062A4B",
            });
        }
    },
    [
        submitting,
        attemptStatus,
        currentQuestion,
        answers,
        persistAnswers,
        applicationNumber,
        attemptId,
        api,
    ]
);


    /* ========================================================
       SUBMIT
    ======================================================== */

    const submitExam =
        useCallback(
            async ({
                automatic = false,
                confirm = true,
            } = {}) => {
                if (
                    !applicationNumber ||
                    !attemptId ||
                    submitLockRef.current ||
                    submitting ||
                    attemptStatus !==
                        "in_progress"
                ) {
                    return;
                }

                if (confirm) {
                    const unanswered =
                        Math.max(
                            0,
                            questions.length -
                                answeredCount
                        );

                    const confirmation =
                        await Swal.fire({
                            icon:
                                automatic ||
                                unanswered >
                                    0
                                    ? "warning"
                                    : "question",

                            title:
                                automatic
                                    ? "Time Expired"
                                    : "Submit Examination?",

                            text:
                                automatic
                                    ? "Your examination time has ended. Your answers will now be submitted."
                                    : unanswered >
                                      0
                                    ? `You have ${unanswered} unanswered question${
                                          unanswered ===
                                          1
                                              ? ""
                                              : "s"
                                      }. Unanswered questions will receive zero marks.`
                                    : "Once submitted, you will not be able to change your answers.",

                            showCancelButton:
                                !automatic,

                            confirmButtonText:
                                automatic
                                    ? "Submit Examination"
                                    : "Submit Now",

                            cancelButtonText:
                                "Continue Examination",

                            confirmButtonColor:
                                automatic
                                    ? BRAND.green
                                    : BRAND.navy,

                            cancelButtonColor:
                                "#7A8494",

                            allowOutsideClick:
                                false,
                        });

                    if (
                        !confirmation.isConfirmed
                    ) {
                        /*
                         * If the timer has reached zero,
                         * don't unlock the timer indefinitely.
                         */
                        if (
                            automatic
                        ) {
                            submitLockRef.current =
                                false;
                        }

                        return;
                    }
                }

                submitLockRef.current =
                    true;

                setSubmitting(
                    true
                );

                setError("");

                try {
                    const payload = {
                        answers:
                            buildSubmitAnswers(
                                answers
                            ),
                    };

                    const response =
                        await api.post(
                            getSubmitEndpoint(
                                applicationNumber,
                                attemptId
                            ),
                            payload
                        );

                    const data =
                        response?.data ||
                        {};

                    applyServerTime(
                        data
                    );

                    const returnedStatus =
                        normalizeAttemptStatus(
                            data?.status ||
                                data?.attempt
                                    ?.status ||
                                "submitted"
                        );

                    const returnedResult =
                        extractResult(
                            data
                        );

                    if (
                        returnedResult
                    ) {
                        setResult(
                            returnedResult
                        );
                    }

                    setAttemptStatus(
                        isCompletedStatus(
                            returnedStatus
                        )
                            ? "submitted"
                            : returnedStatus
                    );

                    clearStoredAnswers(
                        subject
                    );

                    saveStoredAttemptId(
                        subject,
                        attemptId
                    );

                    setQuestionsLoading(
                        false
                    );

                    await showAlert({
                        icon: "success",
                        title:
                            automatic
                                ? "Examination Submitted"
                                : "Examination Completed",
                        text:
                            "Your examination has been submitted successfully and your answers have been graded.",
                        confirmButtonText:
                            "View Result",
                        confirmButtonColor:
                            BRAND.green,
                    });
                } catch (
                    requestError
                ) {
                    console.error(
                        "Entrance examination submission failed:",
                        requestError
                    );

                    const status =
                        requestError
                            ?.response
                            ?.status;

                    if (
                        status ===
                            400 ||
                        status ===
                            409
                    ) {
                        const resultLoaded =
                            await loadResult(
                                attemptId
                            );

                        if (
                            resultLoaded
                        ) {
                            clearStoredAnswers(
                                subject
                            );

                            return;
                        }
                    }

                    submitLockRef.current =
                        false;

                    const message =
                        getErrorMessage(
                            requestError,
                            "We could not submit your examination. Please try again."
                        );

                    setError(
                        message
                    );

                    await showAlert({
                        icon: "error",
                        title:
                            "Submission Failed",
                        text:
                            message,
                        confirmButtonText:
                            "Try Again",
                        confirmButtonColor:
                            BRAND.red,
                    });
                } finally {
                    setSubmitting(
                        false
                    );
                }
            },
            [
                applicationNumber,
                attemptId,
                submitting,
                attemptStatus,
                questions.length,
                answeredCount,
                answers,
                buildSubmitAnswers,
                applyServerTime,
                showAlert,
                loadResult,
                subject,
            ]
        );

    /* ========================================================
       TIMER
    ======================================================== */

    useEffect(() => {
        if (
            !expiresAt ||
            attemptStatus !==
                "in_progress"
        ) {
            return undefined;
        }

        const updateTimer =
            () => {
                const now =
                    Date.now() +
                    serverOffset;

                const expiry =
                    new Date(
                        expiresAt
                    ).getTime();

                if (
                    Number.isNaN(
                        expiry
                    )
                ) {
                    return;
                }

                const seconds =
                    Math.max(
                        0,
                        Math.ceil(
                            (expiry -
                                now) /
                                1000
                        )
                    );

                setRemainingSeconds(
                    seconds
                );

                /*
                 * ==================================================
                 * VOICE ALERTS
                 * ==================================================
                 *
                 * Play exactly once when the timer crosses:
                 *
                 * 20:00
                 * 10:00
                 * 05:00
                 */
                const previous =
                    previousRemainingSecondsRef.current;

                if (
                    previous !== null &&
                    previous > seconds
                ) {
                    const thresholds = [
                        1200,
                        600,
                        300,
                    ];

                    thresholds.forEach(
                        (
                            threshold
                        ) => {
                            if (
                                previous >
                                    threshold &&
                                seconds <=
                                    threshold &&
                                !voiceAlertsPlayedRef.current.has(
                                    threshold
                                )
                            ) {
                                voiceAlertsPlayedRef.current.add(
                                    threshold
                                );

                                playVoiceAlert(
                                    threshold
                                );
                            }
                        }
                    );
                }

                previousRemainingSecondsRef.current =
                    seconds;

                if (
                    seconds <= 0 &&
                    !submitLockRef.current
                ) {
                    submitExam({
                        automatic:
                            true,
                        confirm:
                            true,
                    });
                }
            };

        updateTimer();

        const interval =
            window.setInterval(
                updateTimer,
                1000
            );

        return () =>
            window.clearInterval(
                interval
            );
    }, [
        expiresAt,
        serverOffset,
        attemptStatus,
        submitExam,
    ]);

    /* ========================================================
       RESET VOICE ALERT TRACKING
    ======================================================== */

    useEffect(() => {
        /*
         * If there is no active attempt, clear the
         * threshold state so a newly started exam
         * can use the alerts again.
         */
        if (
            !attemptId ||
            attemptStatus !==
                "in_progress"
        ) {
            voiceAlertsPlayedRef.current =
                new Set();

            previousRemainingSecondsRef.current =
                null;
        }
    }, [
        attemptId,
        attemptStatus,
    ]);

    /* ========================================================
       BACKGROUND STATUS REFRESH
    ======================================================== */

    useEffect(() => {
        if (
            !attemptId ||
            attemptStatus !==
                "in_progress"
        ) {
            return undefined;
        }

        const interval =
            window.setInterval(
                async () => {
                    const data =
                        await refreshAttemptStatus(
                            attemptId,
                            {
                                silent:
                                    true,
                            }
                        );

                    if (!data) {
                        return;
                    }

                    const returnedQuestions =
                        normalizeQuestions(
                            data
                        );

                    if (
                        returnedQuestions.length &&
                        !questions.length
                    ) {
                        setQuestions(
                            returnedQuestions
                        );

                        setQuestionsLoading(
                            false
                        );
                    }

                    const status =
                        normalizeAttemptStatus(
                            data?.status ||
                                data?.attempt
                                    ?.status ||
                                ""
                        );

                    const returnedResult =
                        extractResult(
                            data
                        );

                    if (
                        isCompletedStatus(
                            status
                        ) ||
                        returnedResult
                    ) {
                        const loaded =
                            await loadResult(
                                attemptId
                            );

                        if (
                            loaded
                        ) {
                            setAttemptStatus(
                                "submitted"
                            );
                        }
                    }
                },
                15000
            );

        return () =>
            window.clearInterval(
                interval
            );
    }, [
        attemptId,
        attemptStatus,
        questions.length,
        refreshAttemptStatus,
        loadResult,
    ]);

    /* ========================================================
       NAVIGATION
    ======================================================== */

    const goNext =
        useCallback(
            () => {
                if (
                    currentIndex <
                    questions.length -
                        1
                ) {
                    setCurrentIndex(
                        (
                            value
                        ) =>
                            value + 1
                    );
                }
            },
            [
                currentIndex,
                questions.length,
            ]
        );

    const goPrevious =
        useCallback(
            () => {
                if (
                    currentIndex >
                    0
                ) {
                    setCurrentIndex(
                        (
                            value
                        ) =>
                            value - 1
                    );
                }
            },
            [currentIndex]
        );

    const jumpToQuestion =
        useCallback(
            (index) => {
                setCurrentIndex(
                    index
                );
            },
            []
        );

    /* ========================================================
       EXIT
    ======================================================== */

const handleExit =
    useCallback(
        async () => {
            if (
                attemptId
            ) {
                saveStoredAttemptId(
                    subject,
                    Number(
                        attemptId
                    )
                );
            }

            if (
                answers &&
                Object.keys(
                    answers
                ).length > 0
            ) {
                persistAnswers(
                    answers
                );
            }

            const confirmation =
                await Swal.fire({
                    icon: "warning",
                    title:
                        "Leave Examination?",
                    text:
                        "Your examination attempt will remain active and the timer will continue running. You can return using the same application.",
                    showCancelButton:
                        true,
                    confirmButtonText:
                        "Leave",
                    cancelButtonText:
                        "Continue Exam",
                    confirmButtonColor:
                        BRAND.orange,
                    cancelButtonColor:
                        BRAND.navy,
                    allowOutsideClick:
                        false,
                });

            if (
                confirmation.isConfirmed
            ) {
                /*
                 * Save one final time immediately before
                 * navigation.
                 */
                if (
                    attemptId
                ) {
                    saveStoredAttemptId(
                        subject,
                        Number(
                            attemptId
                        )
                    );
                }

                persistAnswers(
                    answers
                );

                navigate(
                    "/entranceSubjects"
                );
            }
        },
        [
            attemptId,
            subject,
            answers,
            persistAnswers,
            navigate,
        ]
    );

    /* ========================================================
       RESULT VALUES
    ======================================================== */

    const score =
        Number(
            result?.obtained_marks ??
                result?.score ??
                result?.marks_obtained ??
                result?.total_score ??
                0
        );

    const totalMarks =
        Number(
            result?.total_marks ??
                result?.maximum_marks ??
                availableExam?.total_marks ??
                questions.reduce(
                    (
                        total,
                        question
                    ) =>
                        total +
                        Number(
                            question.marks ||
                                1
                        ),
                    0
                )
        );

    const percentage =
        Number(
            result?.percentage ??
                (totalMarks > 0
                    ? (score /
                          totalMarks) *
                      100
                    : 0)
        );

    /* ========================================================
       INVALID SUBJECT
    ======================================================== */

    if (
        !isValidSubject
    ) {
        return (
            <div
                style={{
                    minHeight:
                        "100vh",
                    display:
                        "flex",
                    alignItems:
                        "center",
                    justifyContent:
                        "center",
                    padding: 30,
                    background:
                        BRAND.background,
                    color:
                        BRAND.red,
                    fontWeight: 800,
                }}
            >
                Invalid entrance
                examination
                subject.
            </div>
        );
    }

    /* ========================================================
       LOADING
    ======================================================== */

    if (
        loading ||
        starting
    ) {
        return (
            <LoadingScreen
                subject={
                    subjectInfo.label
                }
                message={
                    starting
                        ? "Securely starting your examination..."
                        : "Preparing your examination..."
                }
            />
        );
    }

    /* ========================================================
       RESULT
    ======================================================== */

    if (
        attemptStatus ===
            "submitted" ||
        attemptStatus ===
            "completed" ||
        result
    ) {
        return (
            <ResultScreen
                subject={
                    subjectInfo.label
                }
                application={
                    application
                }
                applicationNumber={
                    applicationNumber
                }
                score={
                    score
                }
                totalMarks={
                    totalMarks
                }
                percentage={
                    percentage
                }
                navigate={
                    navigate
                }
                result={
                    result
                }
            />
        );
    }

    /* ========================================================
       NO QUESTIONS YET
    ======================================================== */

    if (
        !questions.length ||
        questionsLoading
    ) {
        return (
            <QuestionLoadingScreen
                subject={
                    subjectInfo.label
                }
                remainingSeconds={
                    remainingSeconds
                }
                retryCount={
                    questionRetryCount
                }
                onLeave={
                    handleExit
                }
            />
        );
    }

    /* ========================================================
       MAIN EXAMINATION UI
    ======================================================== */

    return (
        <div className="pee-page">
            <style>{`
                * {
                    box-sizing: border-box;
                }

                @keyframes peeSpin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                @keyframes peePulse {
                    0%, 100% {
                        opacity: 1;
                    }

                    50% {
                        opacity: .55;
                    }
                }

                .pee-page {
                    min-height: 100vh;
                    background:
                        linear-gradient(
                            180deg,
                            #F4F7FB 0%,
                            #FFFFFF 55%,
                            #F4F7FB 100%
                        );
                    color: ${BRAND.text};
                    padding: 18px;
                }

                .pee-container {
                    width: 100%;
                    max-width: 1180px;
                    margin: 0 auto;
                }

                .pee-header {
                    position: relative;
                    overflow: hidden;
                    background:
                        linear-gradient(
                            125deg,
                            ${BRAND.deepNavy},
                            ${BRAND.navyBlue},
                            ${BRAND.blue}
                        );
                    border-radius: 17px;
                    padding: 20px 22px;
                    color: #FFFFFF;
                    box-shadow:
                        0 15px 40px
                        rgba(3,27,53,.14);
                    margin-bottom: 14px;
                }

                .pee-header-content {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                }

                .pee-brand {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    min-width: 0;
                }

                .pee-brand-icon {
                    width: 47px;
                    height: 47px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background:
                        rgba(255,255,255,.1);
                    border:
                        1px solid
                        rgba(255,255,255,.16);
                    color: ${BRAND.gold};
                    font-size: 19px;
                    flex-shrink: 0;
                }

                .pee-eyebrow {
                    color: ${BRAND.gold};
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: 1.4px;
                    margin-bottom: 4px;
                }

                .pee-title {
                    margin: 0;
                    font-size: 21px;
                    line-height: 1.2;
                    font-weight: 900;
                }

                .pee-subtitle {
                    margin: 4px 0 0;
                    color:
                        rgba(255,255,255,.7);
                    font-size: 9px;
                }

                .pee-exit {
                    border:
                        1px solid
                        rgba(255,255,255,.18);
                    background:
                        rgba(255,255,255,.08);
                    color: #FFFFFF;
                    border-radius: 8px;
                    padding: 9px 12px;
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    font-size: 9px;
                    font-weight: 900;
                    cursor: pointer;
                }

                .pee-exit:hover {
                    background: #FFFFFF;
                    color: ${BRAND.navy};
                }

                .pee-info-grid {
                    display: grid;
                    grid-template-columns:
                        minmax(0,1fr)
                        190px;
                    gap: 10px;
                    margin-bottom: 12px;
                }

                .pee-info-card {
                    background: #FFFFFF;
                    border:
                        1px solid
                        ${BRAND.border};
                    border-radius: 11px;
                    padding: 12px 14px;
                    box-shadow:
                        0 7px 22px
                        rgba(6,42,75,.045);
                }

                .pee-info-label {
                    color: #7C8796;
                    font-size: 7px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: .7px;
                    margin-bottom: 4px;
                }

                .pee-info-value {
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 900;
                }

                .pee-timer {
                    background:
                        linear-gradient(
                            135deg,
                            #FFF9E5,
                            #FFFFFF
                        );
                    border:
                        1px solid
                        #F0D98B;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                }

                .pee-timer-icon {
                    color: ${BRAND.orange};
                    font-size: 16px;
                }

                .pee-timer-label {
                    color: #8A6A00;
                    font-size: 7px;
                    font-weight: 900;
                    text-transform: uppercase;
                }

                .pee-timer-value {
                    color: ${BRAND.navy};
                    font-size: 17px;
                    font-weight: 900;
                    letter-spacing: 1px;
                    margin-top: 1px;
                }

                .pee-progress-card {
                    background: #FFFFFF;
                    border:
                        1px solid
                        ${BRAND.border};
                    border-radius: 11px;
                    padding: 12px 14px;
                    margin-bottom: 12px;
                }

                .pee-progress-top {
                    display: flex;
                    justify-content: space-between;
                    gap: 10px;
                    margin-bottom: 7px;
                    color: ${BRAND.navy};
                    font-size: 8px;
                    font-weight: 900;
                }

                .pee-progress-track {
                    width: 100%;
                    height: 7px;
                    overflow: hidden;
                    border-radius: 999px;
                    background: #EDF2F6;
                }

                .pee-progress-fill {
                    height: 100%;
                    border-radius: inherit;
                    background:
                        linear-gradient(
                            90deg,
                            ${BRAND.blue},
                            ${BRAND.green}
                        );
                    transition: width .25s ease;
                }

                .pee-layout {
                    display: grid;
                    grid-template-columns:
                        minmax(0,1fr)
                        245px;
                    gap: 13px;
                    align-items: start;
                }

                .pee-card {
                    background: #FFFFFF;
                    border:
                        1px solid
                        ${BRAND.border};
                    border-radius: 13px;
                    box-shadow:
                        0 10px 30px
                        rgba(6,42,75,.055);
                }

                .pee-question-card {
                    overflow: hidden;
                }

                .pee-question-head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    padding: 14px 16px;
                    border-bottom:
                        1px solid #EDF1F5;
                }

                .pee-question-number {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    color: ${BRAND.navy};
                    font-size: 10px;
                    font-weight: 900;
                }

                .pee-question-number-icon {
                    color: ${BRAND.blue};
                }

                .pee-section-tag {
                    padding: 5px 8px;
                    border-radius: 6px;
                    background: #F0F5FA;
                    color: ${BRAND.blue};
                    font-size: 7px;
                    font-weight: 900;
                    text-transform: uppercase;
                }

                .pee-question-body {
                    padding: 21px;
                }

                .pee-question-text {
                    margin: 0 0 19px;
                    color: ${BRAND.navy};
                    font-size: 15px;
                    line-height: 1.65;
                    font-weight: 800;
                }

                .pee-options {
                    display: grid;
                    gap: 8px;
                }

                .pee-option {
                    width: 100%;
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 12px 13px;
                    border:
                        1px solid
                        ${BRAND.border};
                    border-radius: 9px;
                    background: #FFFFFF;
                    color: ${BRAND.text};
                    text-align: left;
                    cursor: pointer;
                    transition: .18s ease;
                }

                .pee-option:hover {
                    border-color: #A9BACB;
                    background: #FBFCFD;
                }

                .pee-option.selected {
                    border-color: ${BRAND.blue};
                    background: #F3F7FC;
                    box-shadow:
                        inset 3px 0 0
                        ${BRAND.blue};
                }

                .pee-option-letter {
                    width: 27px;
                    height: 27px;
                    border-radius: 7px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    background: #EEF2F6;
                    color: #6F7B8B;
                    font-size: 9px;
                    font-weight: 900;
                }

                .pee-option.selected
                .pee-option-letter {
                    background: ${BRAND.blue};
                    color: #FFFFFF;
                }

                .pee-option-text {
                    padding-top: 3px;
                    color: ${BRAND.text};
                    font-size: 11px;
                    line-height: 1.55;
                    font-weight: 700;
                }

                .pee-question-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    padding: 13px 16px;
                    border-top:
                        1px solid #EDF1F5;
                    background: #FAFBFC;
                }

                .pee-nav-button {
                    border:
                        1px solid
                        ${BRAND.border};
                    border-radius: 8px;
                    background: #FFFFFF;
                    color: ${BRAND.navy};
                    padding: 9px 12px;
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    font-size: 9px;
                    font-weight: 900;
                    cursor: pointer;
                }

                .pee-nav-button.primary {
                    border: none;
                    background:
                        linear-gradient(
                            135deg,
                            ${BRAND.navy},
                            ${BRAND.blue}
                        );
                    color: #FFFFFF;
                }

                .pee-nav-button.submit {
                    border: none;
                    background:
                        linear-gradient(
                            135deg,
                            ${BRAND.green},
                            #3D8F1C
                        );
                    color: #FFFFFF;
                }

                .pee-nav-button:disabled {
                    opacity: .45;
                    cursor: not-allowed;
                }

                .pee-side {
                    padding: 14px;
                }

                .pee-side-title {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    margin: 0 0 10px;
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 900;
                }

                .pee-side-title svg {
                    color: ${BRAND.blue};
                }

                .pee-side-text {
                    margin: 0 0 12px;
                    color: ${BRAND.muted};
                    font-size: 8px;
                    line-height: 1.55;
                }

                .pee-navigator {
                    display: grid;
                    grid-template-columns:
                        repeat(5,1fr);
                    gap: 5px;
                }

                .pee-number {
                    width: 100%;
                    aspect-ratio: 1;
                    border:
                        1px solid
                        ${BRAND.border};
                    border-radius: 6px;
                    background: #FFFFFF;
                    color: #738092;
                    font-size: 8px;
                    font-weight: 900;
                    cursor: pointer;
                }

                .pee-number.current {
                    border-color: ${BRAND.blue};
                    background: ${BRAND.blue};
                    color: #FFFFFF;
                }

                .pee-number.answered {
                    border-color: #CDE4C6;
                    background: #EEF9EA;
                    color: #28721B;
                }

                .pee-number.current.answered {
                    border-color: ${BRAND.green};
                    background: ${BRAND.green};
                    color: #FFFFFF;
                }

                .pee-counts {
                    display: grid;
                    grid-template-columns:
                        repeat(2,1fr);
                    gap: 6px;
                    margin: 12px 0;
                }

                .pee-count {
                    padding: 9px;
                    border-radius: 7px;
                    background: #F7F9FB;
                    border:
                        1px solid #EDF1F5;
                    text-align: center;
                }

                .pee-count-value {
                    color: ${BRAND.navy};
                    font-size: 16px;
                    font-weight: 900;
                }

                .pee-count-label {
                    margin-top: 2px;
                    color: #7A8594;
                    font-size: 6px;
                    font-weight: 900;
                    text-transform: uppercase;
                }

                .pee-security {
                    display: flex;
                    align-items: flex-start;
                    gap: 7px;
                    margin-top: 12px;
                    padding: 10px;
                    border-radius: 7px;
                    background: #F5F8FB;
                    color: ${BRAND.muted};
                    font-size: 7px;
                    line-height: 1.5;
                }

                .pee-security svg {
                    color: ${BRAND.green};
                    flex-shrink: 0;
                }

                .pee-error {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    margin-bottom: 10px;
                    padding: 10px 12px;
                    border:
                        1px solid #F4C6C2;
                    border-radius: 8px;
                    background: #FFF5F4;
                    color: #9F2923;
                    font-size: 9px;
                    font-weight: 800;
                }

                .pee-error-button {
                    border: none;
                    border-radius: 6px;
                    padding: 7px 10px;
                    background: ${BRAND.red};
                    color: #FFFFFF;
                    font-size: 8px;
                    font-weight: 900;
                    cursor: pointer;
                }

                @media (max-width: 850px) {
                    .pee-layout {
                        grid-template-columns: 1fr;
                    }

                    .pee-side {
                        order: 2;
                    }
                }

                @media (max-width: 600px) {
                    .pee-page {
                        padding: 10px;
                    }

                    .pee-header {
                        padding: 16px;
                    }

                    .pee-header-content {
                        align-items: flex-start;
                    }

                    .pee-title {
                        font-size: 18px;
                    }

                    .pee-exit {
                        padding: 8px;
                    }

                    .pee-exit span {
                        display: none;
                    }

                    .pee-info-grid {
                        grid-template-columns: 1fr;
                    }

                    .pee-question-body {
                        padding: 16px;
                    }

                    .pee-question-text {
                        font-size: 13px;
                    }

                    .pee-question-footer {
                        flex-wrap: wrap;
                    }

                    .pee-nav-button {
                        flex: 1;
                        justify-content: center;
                    }
                }
            `}</style>

            <div className="pee-container">
                <header className="pee-header">
                    <div className="pee-header-content">
                        <div className="pee-brand">
                            <div className="pee-brand-icon">
                                <FaGraduationCap />
                            </div>

                            <div>
                                <div className="pee-eyebrow">
                                    Entrance Examination
                                </div>

                                <h1 className="pee-title">
                                    {
                                        subjectInfo.label
                                    }
                                </h1>

                                <p className="pee-subtitle">
                                    Secure online entrance examination
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="pee-exit"
                            onClick={
                                handleExit
                            }
                        >
                            <FaArrowLeft />

                            <span>
                                Leave & Resume Later
                            </span>
                        </button>
                    </div>
                </header>

                {error && (
                    <div className="pee-error">
                        <div>
                            <FaExclamationTriangle
                                style={{
                                    marginRight: 6,
                                }}
                            />

                            {error}
                        </div>

                        <button
                            type="button"
                            className="pee-error-button"
                            onClick={() =>
                                window.location.reload()
                            }
                        >
                            Refresh
                        </button>
                    </div>
                )}

                <div className="pee-info-grid">
                    <div className="pee-info-card">
                        <div className="pee-info-label">
                            Applicant
                        </div>

                        <div className="pee-info-value">
                            {getApplicantName(
                                application
                            )}
                        </div>
                    </div>

                    <div className="pee-info-card pee-timer">
                        <FaClock className="pee-timer-icon" />

                        <div>
                            <div className="pee-timer-label">
                                Time Remaining
                            </div>

                            <div
                                className="pee-timer-value"
                                style={{
                                    color:
                                        remainingSeconds <=
                                        300
                                            ? BRAND.red
                                            : BRAND.navy,
                                }}
                            >
                                {formatSeconds(
                                    remainingSeconds
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pee-progress-card">
                    <div className="pee-progress-top">
                        <span>
                            Examination Progress
                        </span>

                        <span>
                            {answeredCount} of{" "}
                            {questions.length}{" "}
                            answered
                        </span>
                    </div>

                    <div className="pee-progress-track">
                        <div
                            className="pee-progress-fill"
                            style={{
                                width: `${progress}%`,
                            }}
                        />
                    </div>
                </div>

                <div className="pee-layout">
                    <main className="pee-card pee-question-card">
                        <div className="pee-question-head">
                            <div className="pee-question-number">
                                <FaListOl className="pee-question-number-icon" />

                                Question{" "}
                                {currentIndex + 1}{" "}
                                of{" "}
                                {questions.length}
                            </div>

                            {currentQuestion?.section && (
                                <div className="pee-section-tag">
                                    {
                                        currentQuestion.section
                                    }
                                </div>
                            )}
                        </div>

                        <div className="pee-question-body">
                            <h2 className="pee-question-text">
                                {
                                    currentQuestion.question
                                }
                            </h2>

                            <div className="pee-options">
                                {[
                                    "A",
                                    "B",
                                    "C",
                                    "D",
                                ].map(
                                    (
                                        option
                                    ) => {
                                        const selected =
                                            answers[
                                                String(
                                                    currentQuestion.id
                                                )
                                            ] ===
                                            option;

                                        const optionText =
                                            currentQuestion
                                                .options[
                                                option
                                            ];

                                        return (
                                            <button
                                                key={
                                                    option
                                                }
                                                type="button"
                                                className={`pee-option ${
                                                    selected
                                                        ? "selected"
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    selectAnswer(
                                                        option
                                                    )
                                                }
                                                disabled={
                                                    submitting
                                                }
                                            >
                                                <span className="pee-option-letter">
                                                    {option}
                                                </span>

                                                <span className="pee-option-text">
                                                    {
                                                        optionText
                                                    }
                                                </span>

                                                {selected && (
                                                    <FaCheckCircle
                                                        style={{
                                                            marginLeft:
                                                                "auto",
                                                            color:
                                                                BRAND.green,
                                                            marginTop: 4,
                                                        }}
                                                    />
                                                )}
                                            </button>
                                        );
                                    }
                                )}
                            </div>
                        </div>

                        <div className="pee-question-footer">
                            <button
                                type="button"
                                className="pee-nav-button"
                                onClick={
                                    goPrevious
                                }
                                disabled={
                                    currentIndex ===
                                        0 ||
                                    submitting
                                }
                            >
                                <FaArrowLeft />
                                Previous
                            </button>

                            {currentIndex <
                            questions.length -
                                1 ? (
                                <button
                                    type="button"
                                    className="pee-nav-button primary"
                                    onClick={
                                        goNext
                                    }
                                    disabled={
                                        submitting
                                    }
                                >
                                    Next
                                    <FaArrowRight />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="pee-nav-button submit"
                                    onClick={() =>
                                        submitExam({
                                            automatic:
                                                false,
                                            confirm:
                                                true,
                                        })
                                    }
                                    disabled={
                                        submitting
                                    }
                                >
                                    {submitting ? (
                                        <>
                                            <FaSpinner
                                                style={{
                                                    animation:
                                                        "peeSpin .7s linear infinite",
                                                }}
                                            />

                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane />
                                            Submit Examination
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </main>

                    <aside className="pee-card pee-side">
                        <h3 className="pee-side-title">
                            <FaBookOpen />
                            Question Navigator
                        </h3>

                        <p className="pee-side-text">
                            Select any question
                            to move directly
                            to it.
                        </p>

                        <div className="pee-navigator">
                            {questions.map(
                                (
                                    question,
                                    index
                                ) => {
                                    const answered =
                                        Boolean(
                                            answers[
                                                String(
                                                    question.id
                                                )
                                            ]
                                        );

                                    const current =
                                        index ===
                                        currentIndex;

                                    return (
                                        <button
                                            key={
                                                question.id
                                            }
                                            type="button"
                                            className={`pee-number ${
                                                current
                                                    ? "current"
                                                    : answered
                                                    ? "answered"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                jumpToQuestion(
                                                    index
                                                )
                                            }
                                        >
                                            {index +
                                                1}
                                        </button>
                                    );
                                }
                            )}
                        </div>

                        <div className="pee-counts">
                            <div className="pee-count">
                                <div className="pee-count-value">
                                    {
                                        answeredCount
                                    }
                                </div>

                                <div className="pee-count-label">
                                    Answered
                                </div>
                            </div>

                            <div className="pee-count">
                                <div className="pee-count-value">
                                    {
                                        unansweredCount
                                    }
                                </div>

                                <div className="pee-count-label">
                                    Remaining
                                </div>
                            </div>
                        </div>

                        <div className="pee-security">
                            <FaShieldAlt />

                            <span>
                                Your examination
                                is individually
                                secured and
                                connected to your
                                application and
                                examination
                                attempt.
                            </span>
                        </div>

                        <div className="pee-security">
                            <FaLock />

                            <span>
                                Answers are saved
                                on this device
                                until submission.
                            </span>
                        </div>

                        <button
                            type="button"
                            className="pee-nav-button submit"
                            style={{
                                width: "100%",
                                marginTop: 10,
                                justifyContent:
                                    "center",
                            }}
                            onClick={() =>
                                submitExam({
                                    automatic:
                                        false,
                                    confirm:
                                        true,
                                })
                            }
                            disabled={
                                submitting
                            }
                        >
                            {submitting ? (
                                <>
                                    <FaSpinner
                                        style={{
                                            animation:
                                                "peeSpin .7s linear infinite",
                                        }}
                                    />

                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <FaPaperPlane />
                                    Submit Examination
                                </>
                            )}
                        </button>
                    </aside>
                </div>
            </div>
        </div>
    );
};

/* ============================================================
   LOADING SCREEN
============================================================ */

const LoadingScreen = ({
    subject,
    message,
}) => (
    <div
        style={{
            minHeight:
                "100vh",
            display: "flex",
            alignItems:
                "center",
            justifyContent:
                "center",
            background:
                BRAND.background,
            padding: 20,
        }}
    >
        <div
            style={{
                width: "100%",
                maxWidth: 450,
                padding: 32,
                borderRadius: 16,
                background:
                    "#FFFFFF",
                border:
                    `1px solid ${BRAND.border}`,
                textAlign:
                    "center",
                boxShadow:
                    "0 15px 40px rgba(6,42,75,.08)",
            }}
        >
            <div
                style={{
                    width: 48,
                    height: 48,
                    margin:
                        "0 auto 16px",
                    border:
                        "3px solid #E7EDF3",
                    borderTopColor:
                        BRAND.blue,
                    borderRadius:
                        "50%",
                    animation:
                        "peeSpin .75s linear infinite",
                }}
            />

            <h2
                style={{
                    margin:
                        "0 0 6px",
                    color:
                        BRAND.navy,
                    fontSize: 19,
                    fontWeight:
                        900,
                }}
            >
                Preparing{" "}
                {subject}
            </h2>

            <p
                style={{
                    margin: 0,
                    color:
                        BRAND.muted,
                    fontSize: 10,
                    lineHeight:
                        1.7,
                }}
            >
                {message ||
                    "Please wait while we securely prepare your individual examination."}
            </p>

            <div
                style={{
                    marginTop: 17,
                    padding:
                        "9px 12px",
                    borderRadius: 8,
                    background:
                        "#F5F8FB",
                    color:
                        BRAND.muted,
                    fontSize: 8,
                    lineHeight:
                        1.5,
                }}
            >
                Please do not refresh
                or close this page.
            </div>

            <style>{`
                @keyframes peeSpin {
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    </div>
);

/* ============================================================
   QUESTION LOADING SCREEN
============================================================ */

const QuestionLoadingScreen = ({
    subject,
    remainingSeconds,
    retryCount,
    onLeave,
}) => (
    <div
        style={{
            minHeight:
                "100vh",
            background:
                "linear-gradient(180deg,#F4F7FB,#FFFFFF,#F4F7FB)",
            padding: 18,
        }}
    >
        <style>{`
            @keyframes peeSpin {
                to {
                    transform: rotate(360deg);
                }
            }

            @keyframes peePulse {
                0%,100% {
                    opacity: 1;
                }

                50% {
                    opacity: .45;
                }
            }
        `}</style>

        <div
            style={{
                width: "100%",
                maxWidth: 850,
                margin:
                    "30px auto",
            }}
        >
            <div
                style={{
                    overflow:
                        "hidden",
                    borderRadius: 17,
                    background:
                        `linear-gradient(125deg,${BRAND.deepNavy},${BRAND.navyBlue},${BRAND.blue})`,
                    color:
                        "#FFFFFF",
                    padding:
                        "22px 24px",
                    boxShadow:
                        "0 15px 40px rgba(3,27,53,.14)",
                }}
            >
                <div
                    style={{
                        display:
                            "flex",
                        alignItems:
                            "center",
                        justifyContent:
                            "space-between",
                        gap: 15,
                    }}
                >
                    <div
                        style={{
                            display:
                                "flex",
                            alignItems:
                                "center",
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                width: 47,
                                height: 47,
                                borderRadius: 12,
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                justifyContent:
                                    "center",
                                background:
                                    "rgba(255,255,255,.1)",
                                color:
                                    BRAND.gold,
                                fontSize: 19,
                            }}
                        >
                            <FaGraduationCap />
                        </div>

                        <div>
                            <div
                                style={{
                                    color:
                                        BRAND.gold,
                                    fontSize: 8,
                                    fontWeight:
                                        900,
                                    textTransform:
                                        "uppercase",
                                    letterSpacing:
                                        1.2,
                                }}
                            >
                                Entrance Examination
                            </div>

                            <h1
                                style={{
                                    margin:
                                        "4px 0 0",
                                    fontSize: 21,
                                    fontWeight:
                                        900,
                                }}
                            >
                                {subject}
                            </h1>
                        </div>
                    </div>

                    <div
                        style={{
                            textAlign:
                                "right",
                        }}
                    >
                        <div
                            style={{
                                color:
                                    "rgba(255,255,255,.65)",
                                fontSize: 7,
                                fontWeight:
                                    900,
                                textTransform:
                                    "uppercase",
                            }}
                        >
                            Time Remaining
                        </div>

                        <div
                            style={{
                                marginTop: 2,
                                color:
                                    BRAND.gold,
                                fontSize: 17,
                                fontWeight:
                                    900,
                            }}
                        >
                            {formatSeconds(
                                remainingSeconds
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div
                style={{
                    marginTop: 14,
                    background:
                        "#FFFFFF",
                    border:
                        `1px solid ${BRAND.border}`,
                    borderRadius: 15,
                    padding:
                        "55px 25px",
                    textAlign:
                        "center",
                    boxShadow:
                        "0 12px 35px rgba(6,42,75,.06)",
                }}
            >
                <div
                    style={{
                        width: 52,
                        height: 52,
                        margin:
                            "0 auto 18px",
                        border:
                            "3px solid #E7EDF3",
                        borderTopColor:
                            BRAND.blue,
                        borderRadius:
                            "50%",
                        animation:
                            "peeSpin .75s linear infinite",
                    }}
                />

                <h2
                    style={{
                        margin:
                            "0 0 8px",
                        color:
                            BRAND.navy,
                        fontSize: 20,
                        fontWeight:
                            900,
                    }}
                >
                    Loading Examination Questions
                </h2>

                <p
                    style={{
                        maxWidth: 520,
                        margin:
                            "0 auto",
                        color:
                            BRAND.muted,
                        fontSize: 10,
                        lineHeight:
                            1.7,
                    }}
                >
                    Your examination attempt
                    has been secured. We are
                    retrieving your questions.
                    This normally takes only a
                    moment.
                </p>

                <div
                    style={{
                        maxWidth: 450,
                        margin:
                            "20px auto 0",
                        padding:
                            "11px 14px",
                        borderRadius: 8,
                        background:
                            "#F5F8FB",
                        color:
                            BRAND.muted,
                        fontSize: 8,
                        fontWeight:
                            700,
                    }}
                >
                    Secure question retrieval
                    in progress
                    {retryCount > 0
                        ? ` · Attempt ${retryCount}`
                        : ""}
                </div>

                <div
                    style={{
                        marginTop: 13,
                        color:
                            BRAND.green,
                        fontSize: 8,
                        fontWeight:
                            900,
                        animation:
                            "peePulse 1.5s infinite",
                    }}
                >
                    Please remain on this page.
                </div>

                <button
                    type="button"
                    onClick={
                        onLeave
                    }
                    style={{
                        marginTop: 20,
                        border:
                            `1px solid ${BRAND.border}`,
                        borderRadius: 8,
                        background:
                            "#FFFFFF",
                        color:
                            BRAND.navy,
                        padding:
                            "10px 16px",
                        fontSize: 9,
                        fontWeight:
                            900,
                        cursor:
                            "pointer",
                    }}
                >
                    <FaArrowLeft
                        style={{
                            marginRight: 6,
                        }}
                    />

                    Leave & Resume Later
                </button>
            </div>
        </div>
    </div>
);

/* ============================================================
   VIEW TEST PAPER BUTTON
============================================================ */

const ViewEntranceTestPaperButton = ({
    subject,
    application,
    applicationNumber,
    result,
}) => {
    const [
        generating,
        setGenerating,
    ] = useState(false);

    const handleViewPaper =
        async () => {
            if (generating) {
                return;
            }

            setGenerating(
                true
            );

            try {
                const answers =
                    result?.answers ||
                    result?.question_answers ||
                    result?.answer_sheet ||
                    result?.attempt_answers ||
                    [];

                const document = (
                    <EntranceTestPaperPDF
                        subject={
                            subject
                        }
                        application={
                            application
                        }
                        applicationNumber={
                            applicationNumber
                        }
                        result={
                            result
                        }
                        answers={
                            answers
                        }
                        schoolName="PASSION FOR ACADEMIC EXCELLENCE"
                        schoolAddress="Monrovia, Liberia"
                        schoolContacts="Phone: — | Email: —"
                    />
                );

                const blob =
                    await pdf(
                        document
                    ).toBlob();

                const url =
                    URL.createObjectURL(
                        blob
                    );

                window.open(
                    url,
                    "_blank",
                    "noopener,noreferrer"
                );

                window.setTimeout(
                    () => {
                        URL.revokeObjectURL(
                            url
                        );
                    },
                    60000
                );
            } catch (
                error
            ) {
                console.error(
                    "Unable to generate entrance test paper:",
                    error
                );

                Swal.fire({
                    icon: "error",
                    title:
                        "Unable to Generate Paper",
                    text:
                        "The completed entrance examination paper could not be generated. Please try again.",
                    confirmButtonColor:
                        BRAND.navy,
                });
            } finally {
                setGenerating(
                    false
                );
            }
        };

    return (
        <button
            type="button"
            onClick={
                handleViewPaper
            }
            disabled={
                generating
            }
            style={{
                width: "100%",
                marginBottom: 10,
                border: "none",
                borderRadius: 8,
                padding: 13,
                background:
                    `linear-gradient(135deg,${BRAND.blue},${BRAND.navy})`,
                color: "#FFFFFF",
                fontSize: 10,
                fontWeight: 900,
                cursor:
                    generating
                        ? "wait"
                        : "pointer",
                boxShadow:
                    "0 8px 20px rgba(6,58,107,.18)",
            }}
        >
            {generating ? (
                <>
                    <FaSpinner
                        style={{
                            marginRight: 7,
                            animation:
                                "peeSpin .8s linear infinite",
                        }}
                    />

                    Preparing Test Paper...
                </>
            ) : (
                <>
                    <FaBookOpen
                        style={{
                            marginRight: 7,
                        }}
                    />

                    View Entrance Test Paper
                </>
            )}
        </button>
    );
};

/* ============================================================
   RESULT SCREEN
============================================================ */

const ResultScreen = ({
    subject,
    application,
    applicationNumber,
    score,
    totalMarks,
    percentage,
    navigate,
    result,
}) => {
    const passed =
        Number(score) >=
        PASS_MARK;

    const scoreDifference =
        Math.max(
            0,
            PASS_MARK -
                Number(score)
        );

    return (
        <div
            style={{
                minHeight:
                    "100vh",
                padding: 20,
                background:
                    "linear-gradient(180deg,#F4F7FB,#FFFFFF,#F4F7FB)",
            }}
        >
            <style>{`
                @keyframes peeSpin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                .pee-result-card {
                    overflow: hidden;
                }

                .pee-result-status {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 7px 12px;
                    border-radius: 999px;
                    font-size: 9px;
                    font-weight: 900;
                    letter-spacing: .7px;
                    text-transform: uppercase;
                }

                .pee-result-rule {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    flex-wrap: wrap;
                    margin: 0 auto 18px;
                    padding: 10px 13px;
                    border-radius: 9px;
                    background: #F7F9FB;
                    border: 1px solid #EDF1F5;
                    color: ${BRAND.muted};
                    font-size: 9px;
                    font-weight: 800;
                }

                .pee-result-rule strong {
                    color: ${BRAND.navy};
                }

                .pee-result-message {
                    padding: 15px;
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 800;
                    line-height: 1.6;
                    margin-bottom: 12px;
                }

                @media (max-width: 600px) {
                    .pee-result-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>

            <div
                className="pee-result-card"
                style={{
                    width:
                        "100%",
                    maxWidth: 680,
                    margin:
                        "45px auto",
                    padding: 30,
                    background:
                        "#FFFFFF",
                    border:
                        `1px solid ${BRAND.border}`,
                    borderRadius: 18,
                    boxShadow:
                        "0 18px 45px rgba(6,42,75,.09)",
                    textAlign:
                        "center",
                }}
            >
                <div
                    style={{
                        width: 78,
                        height: 78,
                        margin:
                            "0 auto 17px",
                        borderRadius:
                            "50%",
                        display:
                            "flex",
                        alignItems:
                            "center",
                        justifyContent:
                            "center",
                        background:
                            passed
                                ? "#EAF7E5"
                                : "#FFF1F0",
                        color:
                            passed
                                ? BRAND.green
                                : BRAND.red,
                        fontSize: 30,
                    }}
                >
                    {passed ? (
                        <FaTrophy />
                    ) : (
                        <FaCheckCircle />
                    )}
                </div>

                <div
                    className="pee-result-status"
                    style={{
                        background:
                            passed
                                ? "#EAF7E5"
                                : "#FFF1F0",
                        color:
                            passed
                                ? "#28721B"
                                : "#A52822",
                    }}
                >
                    {passed ? (
                        <FaTrophy />
                    ) : (
                        <FaExclamationTriangle />
                    )}

                    {passed
                        ? "Passed"
                        : "Failed"}
                </div>

                <div
                    style={{
                        marginTop: 13,
                        color:
                            BRAND.orange,
                        fontSize: 9,
                        fontWeight: 900,
                        letterSpacing: 1,
                        textTransform:
                            "uppercase",
                    }}
                >
                    Entrance Examination
                    Completed
                </div>

                <h1
                    style={{
                        margin:
                            "6px 0 4px",
                        color:
                            BRAND.navy,
                        fontSize: 25,
                        fontWeight: 900,
                    }}
                >
                    {subject}
                </h1>

                <p
                    style={{
                        margin:
                            "0 0 20px",
                        color:
                            BRAND.muted,
                        fontSize: 10,
                    }}
                >
                    {getApplicantName(
                        application
                    )}
                    {" · "}
                    {applicationNumber}
                </p>

                <div
                    className="pee-result-grid"
                    style={{
                        display:
                            "grid",
                        gridTemplateColumns:
                            "repeat(3,1fr)",
                        gap: 8,
                        marginBottom:
                            13,
                    }}
                >
                    <ResultBox
                        label="Raw Score"
                        value={`${score}/${totalMarks}`}
                    />

                    <ResultBox
                        label="Percentage"
                        value={`${percentage.toFixed(
                            1
                        )}%`}
                    />

                    <ResultBox
                        label="Status"
                        value={
                            passed
                                ? "PASSED"
                                : "FAILED"
                        }
                        valueColor={
                            passed
                                ? BRAND.green
                                : BRAND.red
                        }
                    />
                </div>

                <div className="pee-result-rule">
                    <FaCheckCircle
                        style={{
                            color:
                                passed
                                    ? BRAND.green
                                    : BRAND.orange,
                        }}
                    />

                    <span>
                        Required raw score:
                    </span>

                    <strong>
                        {PASS_MARK}/
                        {totalMarks} or above
                    </strong>
                </div>

                <div
                    style={{
                        width: 150,
                        height: 150,
                        margin:
                            "0 auto 20px",
                        borderRadius:
                            "50%",
                        border:
                            `8px solid ${
                                passed
                                    ? BRAND.green
                                    : BRAND.red
                            }`,
                        display:
                            "flex",
                        alignItems:
                            "center",
                        justifyContent:
                            "center",
                        background:
                            passed
                                ? "#F2F8EF"
                                : "#FFF5F4",
                        boxShadow:
                            "inset 0 0 0 3px #FFFFFF",
                    }}
                >
                    <div>
                        <div
                            style={{
                                color:
                                    passed
                                        ? BRAND.green
                                        : BRAND.red,
                                fontSize: 27,
                                fontWeight: 900,
                            }}
                        >
                            {score}

                            <span
                                style={{
                                    fontSize: 15,
                                    color:
                                        BRAND.muted,
                                }}
                            >
                                /
                                {
                                    totalMarks
                                }
                            </span>
                        </div>

                        <div
                            style={{
                                marginTop: 2,
                                color:
                                    BRAND.muted,
                                fontSize: 8,
                                fontWeight: 900,
                                textTransform:
                                    "uppercase",
                            }}
                        >
                            Final Score
                        </div>
                    </div>
                </div>

                {passed ? (
                    <div
                        className="pee-result-message"
                        style={{
                            background:
                                "#EEF9EA",
                            color:
                                "#28721B",
                        }}
                    >
                        Congratulations! You
                        achieved{" "}
                        <strong>
                            {score}/
                            {
                                totalMarks
                            }
                        </strong>
                        . The required passing
                        mark is{" "}
                        <strong>
                            {PASS_MARK}
                        </strong>
                        . Your entrance examination
                        result is{" "}
                        <strong>
                            PASSED
                        </strong>
                        .
                    </div>
                ) : (
                    <div
                        className="pee-result-message"
                        style={{
                            background:
                                "#FFF1F0",
                            color:
                                "#A52822",
                        }}
                    >
                        Your examination has been
                        completed. You scored{" "}
                        <strong>
                            {score}/
                            {
                                totalMarks
                            }
                        </strong>
                        . The required passing mark
                        is{" "}
                        <strong>
                            {PASS_MARK}/
                            {
                                totalMarks
                            }
                        </strong>
                        . You needed{" "}
                        <strong>
                            {
                                scoreDifference
                            }
                        </strong>{" "}
                        more mark
                        {scoreDifference ===
                        1
                            ? ""
                            : "s"}{" "}
                        to pass.
                    </div>
                )}

                <ViewEntranceTestPaperButton
                    subject={
                        subject
                    }
                    application={
                        application
                    }
                    applicationNumber={
                        applicationNumber
                    }
                    result={
                        result
                    }
                />

                <button
                    type="button"
                    onClick={() =>
                        navigate(
                            "/entranceSubjects"
                        )
                    }
                    style={{
                        width:
                            "100%",
                        border:
                            `1px solid ${BRAND.border}`,
                        borderRadius: 8,
                        padding: 12,
                        background:
                            "#FFFFFF",
                        color:
                            BRAND.navy,
                        fontSize: 10,
                        fontWeight: 900,
                        cursor:
                            "pointer",
                    }}
                >
                    <FaArrowLeft
                        style={{
                            marginRight: 6,
                        }}
                    />

                    Return to Examination
                    Subjects
                </button>
            </div>
        </div>
    );
};

/* ============================================================
   RESULT BOX
============================================================ */

const ResultBox = ({
    label,
    value,
    valueColor,
}) => (
    <div
        style={{
            padding: 13,
            borderRadius: 9,
            background:
                "#F7F9FB",
            border:
                "1px solid #EDF1F5",
        }}
    >
        <div
            style={{
                color:
                    "#7B8796",
                fontSize: 7,
                fontWeight: 900,
                textTransform:
                    "uppercase",
            }}
        >
            {label}
        </div>

        <div
            style={{
                marginTop: 3,
                color:
                    valueColor ||
                    "#062A4B",
                fontSize: 19,
                fontWeight: 900,
            }}
        >
            {value}
        </div>
    </div>
);

export default EntranceExaminationPage;

